import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CartContext } from '../contexts/CartContext';
import productService from '../services/productService';
import { formatPrice, getPrimaryImage, isInStock, getStockDisplay, handleImageError } from '../utils/apiHelpers';

function GalleryCarousel() {
  const { addToCart, loading: cartLoading } = useContext(CartContext);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch latest arrivals from backend
  useEffect(() => {
    const fetchLatestArrivals = async () => {
      try {
        // Use tags=new-arrival as per backend guide
        const response = await productService.getLatestArrivals(8);
        if (response.success && response.products.length > 0) {
          setProducts(response.products);
        } else {
          // Fallback: get any products if no new-arrival tagged products
          const fallbackResponse = await productService.getProducts({ page: 0, size: 8 });
          if (fallbackResponse.success) {
            setProducts(fallbackResponse.products);
          }
        }
      } catch (err) {
        console.error('[GalleryCarousel] Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestArrivals();
  }, []);

  // Re-initialize owl carousel after products load
  useEffect(() => {
    if (!loading && products.length > 0) {
      const initCarousel = () => {
        if (window.$ && window.$.fn.owlCarousel) {
          const owlElement = window.$('.lastest_featured_products');
          if (owlElement.length > 0) {
            // Destroy existing carousel if any
            if (owlElement.data('owl.carousel')) {
              owlElement.trigger('destroy.owl.carousel');
            }
            // Re-initialize carousel
            owlElement.owlCarousel({
              loop: true,
              margin: 30,
              dots: true,
              nav: false,
              autoplay: true,
              autoplayTimeout: 5000,
              autoplayHoverPause: true,
              responsive: {
                0: { items: 1 },
                600: { items: 2 },
                1000: { items: 4 }
              }
            });
          }
        }
      };

      // Small delay to ensure DOM is updated
      setTimeout(initCarousel, 100);
    }
  }, [loading, products]);

  // Handler to add product to cart
  const handleAddToCart = async (e, productId, productName) => {
    e.preventDefault();
    e.stopPropagation();

    const result = await addToCart(productId, 1);
    if (result.success) {
      toast.success(`${productName} added to cart!`);
    } else {
      toast.error(result.error || 'Failed to add to cart');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="lastest_arrivals">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no products
  if (products.length === 0) {
    return null;
  }

  return (
    <div className="lastest_arrivals">
      <div className="container">
        <div className="row">
          <div className="col-12 mb-4">
            <h1>Latest additions to the Gallery</h1>
          </div>

          <div className="col-12">
            <div className="lastest_featured_products owl-carousel owl-theme">
              {products.map((product) => (
                <div key={product.id} className="lastest_arrival_items item">
                  <button
                    className="lastest-addto-cart"
                    onClick={(e) => handleAddToCart(e, product.id, product.name)}
                    disabled={cartLoading || !isInStock(product)}
                    aria-label={`Add ${product.name} to cart`}
                  >
                    <i className="fa fa-shopping-cart"></i>
                  </button>
                  <div className="card">
                    {(() => {
                      const stockInfo = getStockDisplay(product.stock, product.stockStatus);
                      // Show "NEW" badge for in-stock, or stock status for others
                      if (stockInfo.className === 'out-of-stock') {
                        return <span className="product-type out-of-stock">OUT OF STOCK</span>;
                      } else if (stockInfo.className === 'low-stock') {
                        return <span className="product-type low-stock">ONLY {product.stock} LEFT</span>;
                      }
                      return <span className="product-type">NEW</span>;
                    })()}
                    <div className="image-holder">
                      <Link to={`/product-detail/${product.id}`}>
                        <img
                          src={getPrimaryImage(product)}
                          className="card-img-top"
                          alt={product.name}
                          onError={handleImageError}
                        />
                      </Link>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-12 text-center">
                          <h5 className="card-title">{product.name}</h5>
                        </div>
                        <div className="col-12 text-center">
                          <p className="card-text">{formatPrice(product.price)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GalleryCarousel;
